import { resolveElementProps, type Spec, type StateModel, type UIElement } from "@json-render/core";
import type { M360StructuredBlock } from "../m360StructuredContent";

const EVENT_PRIMARY_TYPES = new Set(["EventCard", "EventList"]);
const WRONG_EVENT_ANSWER_TYPES = new Set(["SectionCard", "FacilityCard", "CountSummaryCard"]);

type SpecLink = { label: string; href: string; description?: string };

function elementType(spec: Spec, key: string | undefined): string | null {
  if (!key) {
    return null;
  }

  const element = spec.elements?.[key] as UIElement | undefined;
  return element?.type ?? null;
}

/** Best-effort primary catalog element key for a spec (Stack unwraps to its first child). */
export function getSpecPrimaryElementKey(spec: Spec | null): string | null {
  if (!spec?.root) {
    return null;
  }

  const rootType = elementType(spec, spec.root);
  if (rootType !== "Stack") {
    return spec.root;
  }

  const rootElement = spec.elements?.[spec.root] as UIElement | undefined;
  return rootElement?.children?.[0] ?? null;
}

/** Best-effort primary catalog component for a spec (Stack unwraps to its first child). */
export function getSpecPrimaryComponentType(spec: Spec | null): string | null {
  if (!spec) {
    return null;
  }

  return elementType(spec, getSpecPrimaryElementKey(spec) ?? undefined);
}

export function isEventStructuredBlock(block: M360StructuredBlock | null): block is M360StructuredBlock {
  if (!block || block.type !== "record_list" || block.items.length === 0) {
    return false;
  }

  return block.items.every((item) => item.kind === "event" || item.href?.startsWith("/events/"));
}

/** When tool output includes events but the model rendered a section/facility card instead. */
export function shouldPreferEventCardsOverSpec(spec: Spec | null, promotedBlock: M360StructuredBlock | null): boolean {
  if (!isEventStructuredBlock(promotedBlock)) {
    return false;
  }

  const primaryType = getSpecPrimaryComponentType(spec);
  if (!primaryType || EVENT_PRIMARY_TYPES.has(primaryType)) {
    return false;
  }

  return WRONG_EVENT_ANSWER_TYPES.has(primaryType);
}

function eventRowPropsLackFacilityContext(props: Record<string, unknown>): boolean {
  return !props.facilityName && !props.facilityHref && !props.location;
}

function collectEventRowPropsFromSpec(spec: Spec): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  const stateModel: StateModel = spec.state ?? {};
  const ctx = { stateModel };

  const collectFromElement = (key: string | undefined) => {
    if (!key) {
      return;
    }

    const element = spec.elements?.[key] as UIElement | undefined;
    if (!element) {
      return;
    }

    if (element.type === "EventCard") {
      const props = resolveElementProps((element.props ?? {}) as Record<string, unknown>, ctx);
      rows.push(props);
      return;
    }

    if (element.type === "EventList") {
      const props = resolveElementProps((element.props ?? {}) as Record<string, unknown>, ctx);
      const items = props.items;
      if (Array.isArray(items)) {
        for (const item of items) {
          if (item && typeof item === "object") {
            rows.push(item as Record<string, unknown>);
          }
        }
      }
    }
  };

  collectFromElement(spec.root ?? undefined);
  const rootElement = spec.root ? (spec.elements?.[spec.root] as UIElement | undefined) : undefined;
  rootElement?.children?.forEach(collectFromElement);

  return rows;
}

/** True when the primary EventList resolved to an explicit empty array. */
function specPrimaryEventListIsExplicitlyEmpty(spec: Spec): boolean {
  const primaryKey = getSpecPrimaryElementKey(spec);
  const primaryType = getSpecPrimaryComponentType(spec);
  if (primaryType !== "EventList" || !primaryKey) {
    return false;
  }

  const element = spec.elements?.[primaryKey] as UIElement | undefined;
  if (!element) {
    return false;
  }

  const stateModel: StateModel = spec.state ?? {};
  const props = resolveElementProps((element.props ?? {}) as Record<string, unknown>, { stateModel });
  const items = props.items;
  return Array.isArray(items) && items.length === 0;
}

/** True when EventCard/EventList rows omit facilityName, facilityHref, and location. */
function specEventCardsLackFacilityContext(spec: Spec): boolean {
  const rows = collectEventRowPropsFromSpec(spec);
  return rows.length > 0 && rows.some(eventRowPropsLackFacilityContext);
}

/** True when promoted tool-result rows actually supply facilityName, facilityHref, or location. */
function promotedEventsAddFacilityContext(block: M360StructuredBlock): boolean {
  if (block.type !== "record_list") {
    return false;
  }

  return block.items.some((item) => Boolean(item.facilityName || item.facilityHref || item.location));
}

/**
 * SearchEvents markdown is authoritative when it adds facility/location the model omitted.
 * Do not replace an EventCard/EventList that already has useful status/dates with link-only tool-results.
 */
export function shouldPreferToolResultEventsOverSpec(
  spec: Spec | null,
  promotedBlock: M360StructuredBlock | null
): boolean {
  if (!spec || !isEventStructuredBlock(promotedBlock)) {
    return false;
  }

  const primaryType = getSpecPrimaryComponentType(spec);
  if (!primaryType || !EVENT_PRIMARY_TYPES.has(primaryType)) {
    return false;
  }

  if (specPrimaryEventListIsExplicitlyEmpty(spec)) {
    return true;
  }

  return specEventCardsLackFacilityContext(spec) && promotedEventsAddFacilityContext(promotedBlock);
}

export function hasRenderableStructuredBlock(block: M360StructuredBlock | null): boolean {
  if (!block) {
    return false;
  }

  if (block.type !== "record_list") {
    return true;
  }

  return block.items.length > 0;
}

function normalizeSpecLinks(links: unknown): SpecLink[] | undefined {
  if (!Array.isArray(links)) {
    return undefined;
  }

  const normalized: SpecLink[] = [];
  for (const link of links) {
    const item = link as { label?: string; href?: string; description?: string | null };
    if (!item.label || !item.href) {
      continue;
    }

    normalized.push({
      label: item.label,
      href: item.href,
      ...(item.description ? { description: item.description } : {})
    });
  }

  return normalized.length > 0 ? normalized : undefined;
}

function collectSpecEventLinks(spec: Spec): Map<string, SpecLink[]> {
  const linksByKey = new Map<string, SpecLink[]>();

  const rememberLinks = (key: string | undefined, links: SpecLink[] | undefined) => {
    if (!key || !links?.length) {
      return;
    }

    linksByKey.set(key, links);
  };

  const collectFromProps = (props: Record<string, unknown>) => {
    const href = props.href ? String(props.href) : undefined;
    const title = props.title ? String(props.title) : undefined;
    const links = normalizeSpecLinks(props.links);
    rememberLinks(href, links);
    rememberLinks(title, links);

    const items = props.items;
    if (Array.isArray(items)) {
      for (const item of items) {
        const row = item as Record<string, unknown>;
        collectFromProps(row);
      }
    }
  };

  const collectFromElement = (key: string | undefined) => {
    if (!key) {
      return;
    }

    const element = spec.elements?.[key] as UIElement | undefined;
    if (!element) {
      return;
    }

    if (element.type === "EventCard" || element.type === "EventList") {
      collectFromProps(element.props as Record<string, unknown>);
    }
  };

  collectFromElement(spec.root ?? undefined);
  const rootElement = spec.root ? (spec.elements?.[spec.root] as UIElement | undefined) : undefined;
  rootElement?.children?.forEach(collectFromElement);

  return linksByKey;
}

/** Keep footer chips from EventCard/EventList specs when tool-results replace the rendered spec. */
export function mergeSpecLinksIntoPromotedBlock(
  spec: Spec | null,
  promotedBlock: M360StructuredBlock | null
): M360StructuredBlock | null {
  if (!spec || !promotedBlock || promotedBlock.type !== "record_list") {
    return promotedBlock;
  }

  const specLinksByKey = collectSpecEventLinks(spec);
  if (specLinksByKey.size === 0) {
    return promotedBlock;
  }

  const mergeLinks = (
    existing: { label: string; href: string; description?: string | null }[] | undefined,
    fromSpec: SpecLink[]
  ) => {
    const merged: SpecLink[] = (existing ?? []).map((link) => ({
      label: link.label,
      href: link.href,
      description: link.description ?? undefined
    }));
    for (const link of fromSpec) {
      if (!merged.some((candidate) => candidate.href === link.href && candidate.label === link.label)) {
        merged.push(link);
      }
    }

    return merged.length > 0 ? merged : undefined;
  };

  const items = promotedBlock.items.map((item) => {
    const fromSpec =
      (item.href ? specLinksByKey.get(item.href) : undefined) ??
      (item.title ? specLinksByKey.get(item.title) : undefined);
    if (!fromSpec) {
      return item;
    }

    return {
      ...item,
      links: mergeLinks(item.links, fromSpec)
    };
  });

  return { ...promotedBlock, items };
}
